import { MoreVertical } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { useChannelSettingsContext } from '@sendbird/uikit-react/ChannelSettings/context';
import useLocalization from '@sendbird/uikit-react/hooks/useLocalization';
import { useTranslate } from 'ra-core';
import { SearchTextInput } from '../SearchTextInput';
import { type User, UserList } from '../UserList';
import { ChannelUserCard } from '../ChannelUserCard';
import { useFetchChannelOperators } from '../../../api/hooks/sendbird/useFetchChannelOperators';
import { useToggleOperatorsStatus } from '../../../api/hooks/sendbird/useToggleOperatorsStatus';
import { type UserDto } from '../../../hooks/useGetUsers';
import { Button } from '../../../components/ui/button';
import { ChannelSettingsModal } from './ChannelSettingsModal';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';

interface AllOperatorsModalProps {
  open: boolean;
  onCancel: () => void;
}

export const AllOperatorsModal = ({
  open,
  onCancel,
}: AllOperatorsModalProps) => {
  const translate = useTranslate();
  const { channel } = useChannelSettingsContext();
  const { stringSet } = useLocalization();
  const { data: channelOperators } = useFetchChannelOperators();
  const { mutate: toggleOperatorsStatus, isPending: isTogglePending } =
    useToggleOperatorsStatus();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<UserDto[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  const operatorIds = useMemo(
    () => (channelOperators ?? []).map((operator) => Number(operator.userId)),
    [channelOperators]
  );

  const handleUnregisterOperator = (user: User) => {
    toggleOperatorsStatus({
      channelUrl: channel.url,
      userIds: [user.userId],
      isOperator: false,
    });
  };

  return (
    <ChannelSettingsModal
      open={open}
      onCancel={onCancel}
      title={translate('chat.allOperatorsModalTitle')}
      header={
        <SearchTextInput
          label={translate('chat.searchOperators')}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
        />
      }
      bodyProps={{ className: 'overflow-auto border-y', ref: containerRef }}
      footer={
        <Button type="button" variant="outline" onClick={onCancel}>
          {stringSet.BUTTON__CANCEL}
        </Button>
      }
    >
      <UserList
        searchTerm={searchTerm}
        userIdsFilter={operatorIds}
        containerRef={containerRef}
        renderUser={(user) => (
          <ChannelUserCard
            user={user}
            selectedUsers={selectedUsers}
            setSelectedUsers={setSelectedUsers}
            right={
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger
                  render={
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label={translate('chat.memberActions', {
                        name: user.displayName,
                      })}
                    />
                  }
                >
                  <MoreVertical />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>{user.displayName}</DropdownMenuLabel>
                  <DropdownMenuItem
                    onClick={() => handleUnregisterOperator(user)}
                    disabled={isTogglePending}
                  >
                    {translate('chat.unregisterAsOperator')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            }
          />
        )}
      />
    </ChannelSettingsModal>
  );
};
